import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container, Paper, Typography, Stack, Alert, Button, TextField, CircularProgress, Box
} from "@mui/material";
import { useTranslation } from 'react-i18next';
import { pickServiceName } from '../utils/serviceName';
import { formatMoney } from '../utils/money';
import { api } from "../api"; // твій api.js

export default function PaymentPage() {
  const { t, i18n } = useTranslation();
  const { hash } = useParams();
  const navigate = useNavigate();
  const [linkData, setLinkData] = useState(null);
  const [cardNumber, setCardNumber] = useState("");
  const [expiry, setExpiry] = useState("");
  const [cvc, setCvc] = useState("");
  const [name, setName] = useState("");
  const [tip, setTip] = useState(""); // якщо дозволено змінювати
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState(""); // помилки форми (інлайн)
  const [loadError, setLoadError] = useState(""); // фатальна помилка завантаження
  const [success, setSuccess] = useState(false);

  // 1. Завантаження даних про лінк
  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError("");
      try {
        const data = await api.getPaymentLink(hash);
        setLinkData(data);
      } catch (e) {
        setLoadError(e.message || t('payment.loadError'));
      }
      setLoading(false);
    }
    fetchData();
  }, [hash]);

  // 2. Обробка оплати (замість реального платіжного провайдера — фейк)
  const handlePay = async () => {
    setError("");
    setPaying(true);
    try {
      // todo: тут буде справжній платіж (Stripe, LiqPay, W4P, інше)
      // для демо — просто апдейт статусу
      await api.confirmPayment(hash, {
        cardNumber,
        expiry,
        cvc,
        name,
        tip: Number(tip) || linkData.tip || 0,
      });
      setSuccess(true);
      setTimeout(() => navigate("/pay-success"), 2000);
    } catch (e) {
      setError(e.message || t('payment.payError'));
    }
    setPaying(false);
  };

  if (loading)
    return (
      <Container maxWidth="sm">
        <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
          <CircularProgress color="secondary" />
        </Box>
      </Container>
    );

  if (loadError)
    return (
      <Container maxWidth="sm" sx={{ mt: 6 }}>
        <Alert severity="error">{loadError}</Alert>
      </Container>
    );

  if (!linkData) return null;

  return (
    <Container maxWidth="sm">
      <Paper sx={{ mt: 4, p: 4, bgcolor: "#faf7f2", color: "#1a1a16" }}>
        <Typography variant="h5" gutterBottom>{t('payment.title')}</Typography>
        <Typography sx={{ mb: 2 }}>
          <strong>{t('payment.servicesLabel')}</strong>{" "}
          {linkData.services?.map(s => pickServiceName(s, i18n.language)).join(", ")}
        </Typography>
        <Typography sx={{ mb: 1 }}>
          <strong>{t('payment.amountLabel')}</strong> {formatMoney(linkData.amount)}
        </Typography>
        {linkData.tip > 0 && (
          <Typography sx={{ mb: 1 }}>
            <strong>{t('payment.tipLabel')}</strong> {formatMoney(linkData.tip)}
          </Typography>
        )}

        {/* Поле для зміни суми чайових (якщо потрібно) */}
        <TextField
          label={t('payment.tipFieldLabel')}
          fullWidth
          type="number"
          value={tip}
          onChange={e => setTip(e.target.value)}
          sx={{ mb: 2, mt: 1 }}
          InputProps={{ inputProps: { min: 0, inputMode: 'decimal', step: '0.01' } }}
        />

        {/* Дані картки */}
        <TextField
          label={t('payment.cardNumberLabel')}
          fullWidth
          value={cardNumber}
          onChange={e => setCardNumber(e.target.value)}
          sx={{ mb: 2 }}
        />
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField
            label={t('payment.expiryLabel')}
            placeholder="MM/YY"
            value={expiry}
            onChange={e => setExpiry(e.target.value)}
            sx={{ flex: 1 }}
          />
          <TextField
            label="CVC"
            value={cvc}
            onChange={e => setCvc(e.target.value)}
            sx={{ flex: 1 }}
          />
        </Stack>
        <TextField
          label={t('payment.nameLabel')}
          fullWidth
          value={name}
          onChange={e => setName(e.target.value)}
          sx={{ mb: 2 }}
        />

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{t('payment.success')}</Alert>}

        <Button
          variant="contained"
          fullWidth
          sx={{
            bgcolor: "#c9a24a",
            color: "#1a1a16",
            fontWeight: 500,
            borderRadius: 2,
            textTransform: "none",
            boxShadow: "none",
            '&:hover': { bgcolor: "#977536" }
          }}
          disabled={paying}
          onClick={handlePay}
        >
          {paying ? t('payment.paying') : t('payment.payButton')}
        </Button>
      </Paper>
    </Container>
  );
}
